import * as url from 'url';
import { UpdateSupraScreenConfig } from '@signageos/actions/dist/Supra/Screen/supraScreenActions';
import { checksumString } from '@signageos/lib/dist/Hash/checksum';
import {
	DEFAULT_SUPRA_APP_NAME,
	DEFAULT_SUPRA_SCREEN_APPLET_VERSION,
	DEFAULT_SUPRA_SERVER_STATUS_PORT,
	SUPRA_APPLET_UID,
} from './supraConstants';
import { isNil, omitBy } from 'lodash';

export type IServerSupraScreenConfig = Pick<UpdateSupraScreenConfig, 'servers'>;

export type SupraServer = UpdateSupraScreenConfig['servers'][number];

export interface IAppletSupraScreenCredentials {
	clientID: string;
	clientSecret: string;
}

/**
 * Configuration for the applet @signageos/supra-screen
 * For more details, see the applet documentation.
 */
export interface IAppletSupraScreenConfig extends IAppletSupraScreenCredentials {
	hostname: string;
	secure?: number;
	appName?: string;
	extraArgs?: string;
	url?: string;
}

export interface ISupraCoreAppConfig {
	hostname: string;
	secure: boolean;
	appName: string;
	supraScreenAppletVersion: string;
}

export function getSupraCoreAppDefaultConfig(): Omit<ISupraCoreAppConfig, 'hostname'> {
	return {
		secure: false,
		appName: DEFAULT_SUPRA_APP_NAME,
		supraScreenAppletVersion: DEFAULT_SUPRA_SCREEN_APPLET_VERSION,
	};
}

export function getSupraCoreAppConfigFromServerConfig(server: SupraServer): ISupraCoreAppConfig | null {
	if (!server) {
		return null;
	}
	const configuration: ISupraCoreAppConfig = {
		// TODO following fields might be provided by server configuration in future. Use default values now for simplicity
		...getSupraCoreAppDefaultConfig(),
		hostname: server.hostname,
		secure: server.secure ?? false,
	};
	return configuration;
}

export async function getAvailableSupraServer(servers: SupraServer[]): Promise<SupraServer | null> {
	for (const server of servers) {
		const protocol = server.secure ? 'https:' : 'http:';
		const urlToCheck = `${protocol}//${server.hostname}:${DEFAULT_SUPRA_SERVER_STATUS_PORT}`;
		try {
			const options = { method: 'GET', cache: 'no-cache' } as RequestInit;
			if ('AbortSignal' in globalThis && 'timeout' in AbortSignal) {
				// Old devices might not support AbortSignal.timeout
				options.signal = AbortSignal.timeout(5000);
			}
			const response = await fetch(urlToCheck, options);
			if (response.ok) {
				return server;
			}
		} catch (error) {
			console.error(`Error checking Supra server ${server.hostname}:`, error);
			// Ignore errors and try next server
		}
	}
	return null;
}

export function getSupraScreenCredentials(deviceUid: string, authHash: string): IAppletSupraScreenCredentials {
	// Follows the same logic of generating clientID and clientSecret as in Supra backend
	return {
		clientID: checksumString(deviceUid, 20),
		clientSecret: authHash.substring(0, 40).padStart(40, '0'),
	};
}

export function getSupraAppletChecksum(supraScreenAppletVersion: string, configuration: IAppletSupraScreenConfig): string {
	// Try to replicate similar checksum mechanism of timing as the standard timings on the backend
	return checksumString(SUPRA_APPLET_UID + ':' + supraScreenAppletVersion + ':' + JSON.stringify(configuration));
}

export function parseSupraCoreAppConfigFromDSN(supraDsn: string | undefined): Partial<ISupraCoreAppConfig> | null {
	if (!supraDsn) {
		return null;
	}

	const parsedUrl = url.parse(supraDsn, true);

	const configuration = {
		hostname: parsedUrl.hostname,
		secure: parsedUrl.protocol !== null ? parsedUrl.protocol === 'supras:' : undefined,
		...parsedUrl.query,
	};
	return omitBy<Partial<ISupraCoreAppConfig>>(configuration, isNil);
}
